import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Camera, MapPin, Clock, CheckCircle, LogIn, LogOut, AlertTriangle, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';

function compressImage(videoEl) {
  const canvas = document.createElement('canvas');
  canvas.width = 320;
  canvas.height = 240;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(videoEl, 0, 0, 320, 240);
  return canvas.toDataURL('image/jpeg', 0.5).split(',')[1];
}

export default function ClockInOutCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraMode, setCameraMode] = useState(null); // 'in' or 'out'
  const [stream, setStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [gps, setGps] = useState(null);
  const [gpsError, setGpsError] = useState(null);
  const [permError, setPermError] = useState(null);
  const videoRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/api/attendance/my-status');
      setStatus(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Reminder check: clocked in but not out past 6:30 PM
  const showReminder = status?.record?.clock_in_time && !status?.record?.clock_out_time && status?.is_past_end;

  const startCamera = async (mode) => {
    setCameraMode(mode);
    setCapturedImage(null);
    setGps(null);
    setGpsError(null);
    setPermError(null);
    setShowCamera(true);

    // Request GPS
    if (!navigator.geolocation) {
      setPermError('Geolocation not supported by this browser');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) => {
        setPermError('Location permission denied. Please enable location access to clock ' + (mode === 'in' ? 'in' : 'out') + '.');
        setGpsError(err.message);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );

    // Request Camera
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: 320, height: 240 } });
      setStream(s);
      if (videoRef.current) videoRef.current.srcObject = s;
    } catch (err) {
      setPermError('Camera permission denied. Please enable camera access to clock ' + (mode === 'in' ? 'in' : 'out') + '.');
    }
  };

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(t => t.stop());
      setStream(null);
    }
    setShowCamera(false);
    setCapturedImage(null);
    setCameraMode(null);
  }, [stream]);

  const capture = () => {
    if (!videoRef.current) return;
    const base64 = compressImage(videoRef.current);
    setCapturedImage(base64);
  };

  const submit = async () => {
    if (!capturedImage || !gps) {
      toast.error('Selfie and GPS location are required');
      return;
    }
    setSubmitting(true);
    try {
      const endpoint = cameraMode === 'in' ? '/api/attendance/clock-in' : '/api/attendance/clock-out';
      const { data } = await api.post(endpoint, { selfie: capturedImage, lat: gps.lat, lng: gps.lng });
      toast.success(cameraMode === 'in' ? `Clocked in at ${data.clock_in_time || 'now'}` : `Clocked out at ${data.clock_out_time || 'now'}`);
      stopCamera();
      fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit');
    }
    setSubmitting(false);
  };

  // Attach stream to video element when dialog opens
  useEffect(() => {
    if (stream && videoRef.current) {
      videoRef.current.srcObject = stream;
    }
  }, [stream, showCamera]);

  if (loading) {
    return (
      <Card className="border-slate-200 bg-white animate-pulse">
        <CardContent className="p-5"><div className="h-24 bg-slate-100 rounded-lg" /></CardContent>
      </Card>
    );
  }

  const record = status?.record;
  const clockedIn = !!record?.clock_in_time;
  const clockedOut = !!record?.clock_out_time;
  const isLate = record?.is_late;

  return (
    <>
      <Card className="border-slate-200 bg-white overflow-hidden" data-testid="clock-in-out-card">
        <CardContent className="p-0">
          <div className="flex flex-col sm:flex-row">
            {/* Status side */}
            <div className={`flex-1 p-5 ${clockedOut ? 'bg-emerald-50/50' : clockedIn ? 'bg-blue-50/50' : 'bg-amber-50/30'}`}>
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-4 h-4 text-[#0D2850]" />
                <span className="text-xs font-semibold text-[#0D2850] uppercase tracking-wider">Today's Attendance</span>
                <span className="text-xs text-slate-400 ml-auto">{status?.today}</span>
              </div>

              {!clockedIn ? (
                <div className="flex items-center gap-4">
                  <div>
                    <p className="text-lg font-semibold text-slate-700">Not clocked in yet</p>
                    <p className="text-xs text-slate-400 mt-0.5">Tap Clock In to mark your attendance</p>
                  </div>
                </div>
              ) : !clockedOut ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Badge className={`${isLate ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'} text-xs`}>
                      {isLate ? 'Late' : 'On Time'}
                    </Badge>
                    {record?.half_day_deducted && (
                      <Badge className="bg-red-100 text-red-700 text-xs">Half-day deducted</Badge>
                    )}
                  </div>
                  <p className="text-sm text-slate-600">
                    Clocked in at <span className="font-semibold text-[#0B1B33]">{record.clock_in_time}</span>
                  </p>
                  {showReminder && (
                    <div className="flex items-center gap-2 mt-2 p-2 bg-amber-100 rounded-md">
                      <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                      <p className="text-xs text-amber-700 font-medium">It's past 6:30 PM. Don't forget to clock out!</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                    <p className="text-sm font-semibold text-emerald-700">Attendance Complete</p>
                  </div>
                  <div className="flex gap-6 text-sm text-slate-600">
                    <span>In: <span className="font-semibold text-[#0B1B33]">{record.clock_in_time}</span></span>
                    <span>Out: <span className="font-semibold text-[#0B1B33]">{record.clock_out_time}</span></span>
                  </div>
                  {isLate && <Badge className="bg-amber-100 text-amber-700 text-xs">Late arrival</Badge>}
                </div>
              )}
            </div>

            {/* Action side */}
            <div className="flex items-center justify-center p-5 border-t sm:border-t-0 sm:border-l border-slate-100 sm:w-[200px]">
              {!clockedIn ? (
                <Button
                  className="bg-[#0D2850] hover:bg-[#0B2246] text-white gap-2 w-full"
                  onClick={() => startCamera('in')}
                  data-testid="clock-in-btn"
                >
                  <LogIn className="w-4 h-4" /> Clock In
                </Button>
              ) : !clockedOut ? (
                <Button
                  variant="outline"
                  className="border-rose-200 text-rose-600 hover:bg-rose-50 gap-2 w-full"
                  onClick={() => startCamera('out')}
                  data-testid="clock-out-btn"
                >
                  <LogOut className="w-4 h-4" /> Clock Out
                </Button>
              ) : (
                <div className="text-center">
                  <CheckCircle className="w-8 h-8 text-emerald-400 mx-auto" />
                  <p className="text-xs text-slate-400 mt-1">All done for today</p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Camera Dialog */}
      <Dialog open={showCamera} onOpenChange={(open) => { if (!open) stopCamera(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {cameraMode === 'in' ? 'Clock In' : 'Clock Out'} - Selfie Verification
            </DialogTitle>
            <DialogDescription>
              Take a selfie and confirm your location to {cameraMode === 'in' ? 'clock in' : 'clock out'}.
            </DialogDescription>
          </DialogHeader>

          {permError ? (
            <div className="p-6 text-center space-y-3">
              <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto" />
              <p className="text-sm text-slate-600">{permError}</p>
              <Button variant="outline" onClick={stopCamera}>Close</Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="relative bg-black rounded-lg overflow-hidden aspect-[4/3]">
                {!capturedImage ? (
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-full object-cover mirror"
                    style={{ transform: 'scaleX(-1)' }}
                  />
                ) : (
                  <img
                    src={`data:image/jpeg;base64,${capturedImage}`}
                    alt="Captured selfie"
                    className="w-full h-full object-cover"
                    style={{ transform: 'scaleX(-1)' }}
                  />
                )}
              </div>

              {/* GPS Status */}
              <div className="flex items-center gap-2 text-sm">
                <MapPin className={`w-4 h-4 ${gps ? 'text-emerald-500' : 'text-slate-300 animate-pulse'}`} />
                {gps ? (
                  <span className="text-emerald-600 text-xs">
                    Location captured ({gps.lat.toFixed(4)}, {gps.lng.toFixed(4)})
                  </span>
                ) : (
                  <span className="text-slate-400 text-xs">Acquiring GPS location...</span>
                )}
              </div>

              <div className="flex gap-2">
                {!capturedImage ? (
                  <Button className="flex-1 bg-[#0D2850] hover:bg-[#0B2246] gap-2" onClick={capture} data-testid="capture-selfie-btn">
                    <Camera className="w-4 h-4" /> Capture Selfie
                  </Button>
                ) : (
                  <>
                    <Button variant="outline" className="flex-1" onClick={() => setCapturedImage(null)}>
                      Retake
                    </Button>
                    <Button
                      className="flex-1 bg-[#0D2850] hover:bg-[#0B2246] gap-2"
                      onClick={submit}
                      disabled={submitting || !gps}
                      data-testid="confirm-clock-btn"
                    >
                      {submitting ? 'Submitting...' : `Confirm ${cameraMode === 'in' ? 'Clock In' : 'Clock Out'}`}
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
